import get from 'lodash/get';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import { FC } from 'react';

import {
  Campaign,
  ChannelOptNodeData,
  ChildNode,
  Node,
  NODE_TYPES,
  SendNodeData,
  WORKFLOW_ACTIONS,
} from 'modules/workflows';
import {
  CustomEvent,
  SendEmailEditor,
  SendNodeResources,
  SendPushEditor,
  SendSMSEditor,
} from '../../sendNodeEditors';
import { ChildNodeResources } from '../types';

export const combineCampaign = (
  childNode: ChildNode,
  nextNodeData: SendNodeData,
): { campaign?: Campaign } => {
  if (nextNodeData.resource?.campaign) {
    return {
      campaign: {
        // NOTE: Avoid placing remote conversions in state where customConversions are tracked locally
        ...omit(childNode.nodeData.resource.campaign, ['conversions']),
        ...(nextNodeData.resource.campaign || {}),
      },
    };
  }
  return {};
};

export const getUpdatedNodeData = (
  channelOptNodeData: ChannelOptNodeData,
  childNodeType: string,
  nextNodeData: SendNodeData,
): ChannelOptNodeData => {
  const { childNodes = [] } = channelOptNodeData;

  const updateChildNodes = childNodes.reduce((acc: ChildNode[], childNode) => {
    const nextNodeCampaign = combineCampaign(childNode, nextNodeData);
    const mergedNode =
      childNode.type === childNodeType
        ? merge({}, childNode, { nodeData: nextNodeData })
        : childNode;
    // Necessary as we do not want a *true* merge, but an overwrite of campaign data
    const updatedNode =
      childNode.type === childNodeType
        ? {
            ...mergedNode,
            nodeData: {
              ...mergedNode.nodeData,
              resource: {
                ...(mergedNode.nodeData.resource || {}),
                ...nextNodeCampaign,
              },
            },
          }
        : childNode;

    return [...acc, updatedNode];
  }, []);

  return { ...channelOptNodeData, childNodes: updateChildNodes };
};

export const getChannelEditorComponent = (channelEditorType: string) => {
  const channelEditorsByType: Record<string, FC<React.PropsWithChildren<any>>> = {
    [NODE_TYPES.SEND_EMAIL]: SendEmailEditor,
    [NODE_TYPES.SEND_PUSH]: SendPushEditor,
    [NODE_TYPES.SEND_SMS]: SendSMSEditor,
  };
  return channelEditorsByType[channelEditorType];
};

export const getChannelEditorProps = (
  channelEditorType: string,
  node: Node,
  resources: unknown,
) => {
  const { x, y, id } = node;
  const childNodes = (node.nodeData as ChannelOptNodeData)?.childNodes || [];
  const childNode = childNodes.find(({ type }) => type === channelEditorType);

  let childResources: SendNodeResources = {};
  if (typeof resources === 'object') {
    const fetchedResources = get(
      resources as ChildNodeResources,
      `[${channelEditorType}].campaign.data`,
      childResources,
    ) as SendNodeResources;
    const fetchedEvents = get(
      resources as ChildNodeResources,
      `[${channelEditorType}].customEvents`,
      [],
    ) as CustomEvent[];
    childResources = {
      ...fetchedResources,
      customEvents: fetchedEvents,
    };
  }
  return {
    isTitleEditable: false,
    shouldShowSTO: false,
    isNoteEnabled: false,
    node: { x, y, id, ...childNode },
    nodeData: childNode?.nodeData,
    nodeType: childNode?.type,
    resources: childResources,
    deferredNodeClass: WORKFLOW_ACTIONS.CHANNEL_OPT,
    shouldShowDeliverySectionHeader: true,
    shouldShowQuietHours: true,
    shouldShowRateLimit: true,
  };
};

export const getChannelClonedFromNodeData = (
  selectedEditor: string,
  clonedFromNodeData: ChannelOptNodeData,
) => {
  const childNode = clonedFromNodeData?.childNodes?.find((child) => {
    return child.type === selectedEditor;
  });
  const campaignId = childNode?.nodeData?.campaignId;
  const campaignResources = clonedFromNodeData?.resource?.resources;
  const clonedCampaignResource = campaignResources?.find(
    (resource) => resource?.campaign?.id === campaignId,
  );
  return {
    resource: clonedCampaignResource || {},
  };
};
